/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { VertexColors, SphereGeometry, Points, Mesh, Vector3, Intersection } from 'three';
import { Director, Stage, Materials, Graph, ColorMaps, AuxiliaryLine, Scene, Pointer, Tracker, DefaultCaps, DefaultNavigators, CapNames, SceneCompass } from '@sick-davinci/glow';

import { TextField } from '@sick-davinci/basic-elements';

export const dependencies = [TextField];  // avoid wepack removal

/**
 * Demo:    DistanceMeasurement
 * Description:
 *   This demo application shows user-/object interaction by measuring the distance between 2 points.
 *   Click on a point to start measurent. Click on a 2nd point to finish the measurement.
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - Graph
 *  - Tracker (Follow mouse position)
 *  - Pointer (Raycasting)
 */
export const run = ((viewParent: HTMLElement) => {

  // * Setup the stage *
  const stage = new Stage(viewParent, {
    cameras: [
      {
        name: 'default',
        from: [20, 20, 20],
        to: [0, 0, 0],
        up: [0, 0, 1],
        fov: 60,
      },
    ],
    scenes: [{
      name: 'default',
      }],
    viewports: [
      {
        name: 'default',
        scene: 'default',
        camera: 'default',
        navigator: 'orbit',
        bgcolor: 0x393939,
      },
    ],
  });

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit', {rotateAround: {up: new Vector3(0, 0, 1)}});

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'default');

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);

  director.handle(CapNames.AXIS, { axisLength: 15 });

  director.handle(CapNames.LIGHT, { payload: { lightType: 'spot', color: 0x777777 }, orientation: { position: [10, 10, 0] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0x777777 } });

  stage.start(); // start visualizing data

  // * Add a graph object
  const graph = new Graph();
  graph.setColormap(ColorMaps.generate(ColorMaps.models.jet, 256, 1));

  graph.setCountX(20);
  graph.setCountY(20);
  graph.setMinX(-10);
  graph.setMaxX(10);
  graph.setMinY(-10);
  graph.setMaxY(10);

  const geo = graph.createGraphXY(
    (x, y) => Math.sin(Math.sqrt(x * x + y * y) * 0.3) * 2,
    (x, y) => Math.sin(Math.sqrt(x * x + y * y) * 0.3) / 2 + 0.5,
    false,
  );

  const material = Materials.createPointMaterial(
    {
      shape: 'rhombus',
      params: {
        size: 0.4, sizeAttenuation: true, vertexColors: VertexColors,
      },
    },
  );
  material.needsUpdate = true;
  const points = new Points(geo, material);
  stage.applyThreeDirect((scene: Scene) => {
    scene.add(points);
  });

  // * Create interaction objects
  const geometry = new SphereGeometry(0.1, 10, 10);
  const sphere = new Mesh(geometry, Materials.create({ type: 'normal', params: { transparent: true, opacity: 0.8 } }));
  sphere.userData.imaginary = true;
  sphere.visible = false;
  const line = new AuxiliaryLine();

  stage.applyThreeDirect((scene: Scene) => {
    scene.add(sphere);
    scene.add(line);
  });

  const staticP = new Vector3();
  function getPos(intersection: Intersection) {
    const pos = (intersection.object as any).geometry.getAttribute('position');
    staticP.fromArray(pos.array, intersection.index * 3);
    staticP.applyMatrix4(intersection.object.matrix);
    return staticP;
  }

  // * Setup user interaction
  const pointer = new Pointer(stage.getCamera('default'));
  const tracker = new Tracker(stage);
  tracker.onMove(
    (x, y) => {
      const p = pointer.to3DSpace(x, y);
      sphere.position.x = p.x;
      sphere.position.y = p.y;
      sphere.position.z = p.z;
      line.setP2(p);
  });

  tracker.onFirstClick(
    (intersection) => {
      const point = getPos(intersection);
      pointer.changePoint(point);
      sphere.position.x = point.x;
      sphere.position.y = point.y;
      sphere.position.z = point.z;
      line.setP1(point);
      line.setP2(point);
      line.enable(true);
      sphere.visible = true;
  });

  tracker.onNextClick(
    (intersection) => {
      const point = getPos(intersection);
      pointer.changePoint(point);
      sphere.position.x = point.x;
      sphere.position.y = point.y;
      sphere.position.z = point.z;
      line.setP2(point);
      (document.getElementById('len') as TextField).value = line.getLength().toString();
      tracker.start();
  });

  tracker.onCancel(
    () => {
      sphere.visible = false;
      line.enable(false);
    },
  );
  tracker.start();
});
